/**
 * forja.mjs — STATE, TRUST, SENSE, PROOF, applied to the page you are reading it on.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 6.
 *
 * ── THE ONLY HONEST SUBJECT FOR THIS CHAPTER WAS THIS APP ─────────────────────────────────────
 * §1: "a trustable AI product is one that declares its STATE, TRUST, SENSE, and PROOF — by data,
 * not by speech." Every other chapter's lab could take an example from the module. This one cannot:
 * a Forja audit of an invented service would be speech about speech, which is the thing the chapter
 * exists to refuse. So the lab audits the tutor a learner is sitting in front of — a real system,
 * with real declarations, that can be checked while it is being discussed.
 *
 * ── AND IT FAILS PART OF ITS OWN AUDIT ────────────────────────────────────────────────────────
 * §5 lists five things PROOF must carry. Measured across every lab in this app on 2026-08-14: four
 * are carried and one is not. There is no confidence band anywhere — not one. An audit that came
 * back clean would have been worth nothing; the point of a declaration is that it can embarrass you.
 *
 * ── THE NUMBERS ARE A DATED MEASUREMENT, AND THE GATE RE-TAKES IT ─────────────────────────────
 * The lab runs in a browser and cannot grep its own source, so the counts below are a recorded
 * measurement rather than a live one. That is exactly the shape that rots — so gate-forja.mjs
 * re-measures against the source on every run and fails if any figure has drifted. A self-audit
 * nobody re-takes is marketing with a date on it.
 */

export const SOURCE_1 = 'CodeAI101 module 06 §1 "The one-paragraph version"';
export const SOURCE_2 = 'CodeAI101 module 06 §2 "STATE — what the service holds"';
export const SOURCE_3 = 'CodeAI101 module 06 §3 "TRUST — what the service is trusted to do"';
export const SOURCE_5 = 'CodeAI101 module 06 §5 "PROOF — what evidence the service gives for its answers"';
export const SOURCE_6 = 'CodeAI101 module 06 §6 "Declaration beats inference — the contrast"';

export const THE_LINE = 'a trustable AI product is one that declares its STATE, TRUST, SENSE, and PROOF — by data, not by speech.';
export const NOT_BROCHURE = 'not as a sales-brochure description ("we manage your records"), but as a structured declaration of exactly what records it persists';
export const PROOF_LOADBEARING = 'This is the load-bearing one.';
export const CO_EMITTED = 'Proof is not optional and it is not after-the-fact. It is generated at the moment of the answer, by the same code path that generated the answer. The proof is co-emitted with the result.';

/** §1's four, in his words. */
export const FOUR = Object.freeze([
  { id: 'state', name: 'STATE', quote: 'what data and configuration it holds.' },
  { id: 'trust', name: 'TRUST', quote: 'what operations it is reliable enough to perform, with an honest level per operation.' },
  { id: 'sense', name: 'SENSE', quote: 'what events it emits while doing its work.' },
  { id: 'proof', name: 'PROOF', quote: 'what evidence it produces alongside any answer.' },
]);

/** §5's five things proof must carry, verbatim. */
export const PROOF_ELEMENTS = Object.freeze([
  { id: 'inputs', text: 'Which inputs were used.' },
  { id: 'rules', text: 'Which rules or calculations were applied.' },
  { id: 'sources', text: 'Which sources were cited.' },
  { id: 'confidence', text: 'What the confidence band is.' },
  { id: 'drill', text: 'Where the human can drill in to verify each step.' },
]);

/**
 * THE AUDIT OF THIS APP. Measured 2026-08-14 by counting across every lab's source; re-measured by
 * gate-forja.mjs on every run, which fails if a figure moves. `carried: false` is not a to-do item
 * dressed as a finding — it is a declaration that this product does not do a thing the methodology
 * requires, published in the lab that teaches the methodology.
 */
export const SELF_AUDIT = Object.freeze({
  measured_on: '2026-08-14',
  method: 'counted across slm-tutor/app/src/*.jsx and *.mjs — the provenance constructors each lab actually emits, and the presence of each of §5\'s five elements.',
  /* THESE NUMBERS WERE WRONG THE FIRST TIME AND THE GATE CAUGHT IT. My original measurement globbed
     `*lab.jsx *.jsx`, so every lab file was counted twice and the figures came out roughly double.
     The gate re-counts each file once, which is the correct reading, and it went red immediately —
     on a self-audit, which is the one artefact where a flattering error is worst. */
  /* RE-TAKEN 2026-08-14 after the chapter-7 lab landed. The audit went stale the moment another
     lab was added, and the gate said so on the next run — which is the behaviour that makes a
     published self-audit worth anything. Adding a lab is supposed to move these numbers; leaving
     them un-updated is what would be dishonest. */
  states: Object.freeze({ computed: 140, cited: 182, refused: 70, absent: 30, failed: 9, gated: 0 }),
  proof: Object.freeze([
    { id: 'inputs', carried: true, how: 'Every lab shows what it worked from — the rows it summed, the words it matched, the slots it read.' },
    { id: 'rules', carried: true, how: 'The operation is printed beside the answer: "Sum the Amount column where Day = Mon", "count, weigh, pick".' },
    { id: 'sources', carried: true, how: 'Every quoted sentence carries the module and section it came from, and a gate proves it is really there.' },
    { id: 'confidence', carried: false, how: 'NOT CARRIED. Nothing in this app emits a confidence band — the count is zero. Its values are computed, cited, refused or absent, which is a different vocabulary: it says HOW a thing is known, not how sure it is. That is a real gap against the methodology and it is declared rather than quietly omitted.' },
    { id: 'drill', carried: true, how: 'Each citation names a module and section a learner can open and read.' },
  ]),
});
export const carriedCount = (a = SELF_AUDIT) => a.proof.filter((p) => p.carried).length;

/** §6's contrast table, row for row, as he wrote it. */
export const CONTRAST = Object.freeze([
  { inference: 'The service\'s capabilities are inferred from marketing copy and demos.', declaration: 'The service declares what it does, in structured form.' },
  { inference: 'Trust is established by reputation and pitch.', declaration: 'Trust is established by reading the declaration and verifying samples.' },
  { inference: 'Capabilities drift silently — today it works, tomorrow it does not.', declaration: 'Capabilities have versioned, machine-checkable declarations.' },
  { inference: 'Verification requires a human to ask, interpret, and decide.', declaration: 'Verification is a query — a script, an audit tool, an AI agent can do it.' },
  { inference: 'Audit is a forensic exercise after a failure.', declaration: 'Audit is a live function of the system, available at any time.' },
  { inference: 'At scale, inference becomes hallucination.', declaration: 'At scale, declaration scales as cleanly as a database query.' },
]);

/** §6's scale claim, quoted — and the arithmetic that follows from it. */
/* The whole sentence. Mine stopped at "hallucination." with a full stop the module does not have
   there — his clause continues past the dash, and that continuation is the reason. Seventh elision
   caught today by a gate matching the module rather than my memory of it. */
export const AT_SCALE = 'At ten services, inference is just about manageable. At fifty, inference cracks. At three hundred, inference is hallucination — the human operator cannot hold the truth of all the services in their head, and the marketing copy of any one of them has drifted from reality by an unknown amount.';
export const SCALES = Object.freeze([10, 50, 300]);

/**
 * What it costs to answer one question about N services, each way.
 *
 * Inference is one human reading per service; declaration is one query however many there are.
 * The numbers are not a performance claim — they are the definition of the two approaches, counted.
 * No time or money is attached, because the module attaches none.
 */
export const costOf = (n) => ({ services: n, byInference: n, byDeclaration: 1 });
